import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import Typography from "@/design-system/Typography";
import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import { useModalStackContext } from "@/src/contexts/ModelStackContext";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type PropsWithStyleX, type QueryXsMax, colors } from "../../styles/tokens.stylex";

export type ModalProps = PropsWithStyleX & {
    id?: string;
    ref?: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    stylexHeaderTitle?: stylex.StyleXStyles;
    stylexHeader?: stylex.StyleXStyles;
    stylexMain?: stylex.StyleXStyles;
    stylexFooter?: stylex.StyleXStyles;
    styleXCloseBtn?: stylex.StyleXStyles;
    onClose: () => void;
};

export default function Modal(props: ModalProps) {
    const [isClient, setIsClient] = useState(false);
    const { onCloseModal, onOpenModal } = useModalStackContext();

    useEffect(() => {
        onOpenModal();
        document.body.style.overflow = "hidden";

        setIsClient(true);
        return () => {
            document.body.style.overflow = "";
            onCloseModal();
        };
    }, []);

    return createPortal(
        <div id={props.id} {...stylex.props(styles.modalOverlay, isClient && styles.show)}>
            <section {...stylex.props(styles.modalContent, props.styleX)} ref={props.ref}>
                {props.header !== undefined && (
                    <header {...stylex.props(styles.padding, styles.header, props.stylexHeader)}>
                        {typeof props.header === "string" ? (
                            <Typography
                                textElement="h1"
                                fontStyle="hS"
                                styleX={[styles.headerTitle, props.stylexHeaderTitle]}
                            >
                                {props.header}
                            </Typography>
                        ) : (
                            props.header
                        )}
                    </header>
                )}
                <main {...stylex.props(styles.padding, styles.main, props.stylexMain)}>{props.children}</main>
                {props.footer && (
                    <footer {...stylex.props(styles.padding, styles.footer, props.stylexFooter)}>
                        {props.footer}
                    </footer>
                )}
                <CloseIconSlimButton
                    styleX={[styles.close, props.styleXCloseBtn]}
                    onClick={(ev) => {
                        ev.stopPropagation();
                        props.onClose();
                    }}
                />
            </section>
        </div>,
        document.body,
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    modalOverlay: {
        padding: {
            default: ComposableSpacing.s10,
            [xsMax]: 0,
        },
        backgroundPosition: "0% 0%",
        transition: "opacity 0.3s, transform 0.3s",
        alignItems: "center",
        backgroundColor: "#000000CC",
        backgroundRepeat: "no-repeat",
        backgroundSize: "padding-box",
        display: "flex",
        justifyContent: "center",
        position: "fixed",
        transform: "translate3d(0, -100%, 0)",
        zIndex: 90,
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
    },
    show: {
        transform: "translate3d(0, 0, 0)",
    },
    modalContent: {
        borderRadius: "1em",
        backgroundColor: colors.bg_Primary,
        boxShadow: "0 0.2em 0.6em rgba(0, 0, 0, 0.1)",
        position: "relative",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
        maxWidth: "37.5rem",
    },
    header: {
        alignItems: "start",
        display: "flex",
        justifyContent: "space-between",
    },
    headerTitle: {
        margin: 0,
        padding: 0,
        paddingRight: "1em",
    },
    main: {
        overflow: "auto",
        scrollbarWidth: "none",
        willChange: "scroll-position",
        "::-webkit-scrollbar": { display: "none" },
    },
    footer: {
        borderTopColor: colors.fg_Tertiary,
        borderTopStyle: "solid",
        borderTopWidth: "1px",
    },
    padding: {
        padding: {
            default: "2em",
            [xsMax]: "1.4em",
        },
    },
    close: {
        padding: 0,
        borderStyle: "none",
        color: {
            default: colors.fg_Tertiary,
            ":hover": colors.fg_Primary,
        },
        position: "absolute",
        height: "1rem",
        right: 20,
        top: ComposableSpacing.s3,
        width: "1rem",
    },
});
